if (!customElements.get('chastar-header')) {
  customElements.define('chastar-header', class extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      this.cache = new Map();
      this.sequence = 0;
      this.selectedIndex = -1;
      this.host = this.closest('.shopify-section') || this;
      this.searchDialog = this.querySelector('.chh-dialog--search');
      this.menuDialog = this.querySelector('.chh-dialog--menu');
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-chh-results]');
      this.status = this.querySelector('[data-chh-status]');
      this.intro = this.querySelector('[data-chh-intro]');
      this.reset = this.querySelector('[data-chh-reset]');
      this.classList.add('chh-is-enhanced');
      this.previousScroll = window.scrollY;
      this.host.style.position = this.dataset.sticky === 'none' ? 'relative' : 'sticky';
      this.host.style.top = '0px';
      this.host.style.zIndex = '30';
      this.listen(this, 'click', (event) => this.handleClick(event));
      this.listen(this, 'toggle', (event) => this.handleDropdown(event), { capture: true });
      this.listen(this, 'change', (event) => {
        if (event.target.matches('[data-chh-localization]')) event.target.form.requestSubmit();
      });
      this.listen(this.input, 'input', () => this.handleInput());
      this.listen(this.input, 'keydown', (event) => this.handleSearchKeys(event));
      this.listen(document, 'keydown', (event) => this.handleDocumentKeys(event));
      this.listen(document, 'click', (event) => {
        if (!this.contains(event.target)) this.closeDropdowns();
      });
      [this.searchDialog, this.menuDialog].forEach((dialog) => {
        this.listen(dialog, 'click', (event) => {
          if (event.target !== dialog) return;
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
        });
        this.listen(dialog, 'close', () => this.handleClose(dialog));
      });
      this.desktop = window.matchMedia('(min-width: 1100px)');
      this.hover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.dropdownTimers = new Map();
      this.querySelectorAll('.chh-links--desktop .chh-dropdown').forEach((details) => {
        this.listen(details, 'pointerenter', (event) => {
          if (!this.desktop.matches || !this.hover.matches || event.pointerType === 'touch') return;
          if (this.searchDialog.open || this.menuDialog.open) return;
          this.cancelDropdownClose(details);
          this.closeDropdowns(details);
          this.updateMenuSpace();
          details.open = true;
        });
        this.listen(details, 'pointerleave', (event) => {
          if (!this.desktop.matches || !this.hover.matches || event.pointerType === 'touch') return;
          this.cancelDropdownClose(details);
          this.dropdownTimers.set(details, setTimeout(() => {
            this.dropdownTimers.delete(details);
            if (!details.contains(document.activeElement)) details.open = false;
          }, 200));
        });
        this.listen(details, 'focusin', () => this.cancelDropdownClose(details));
        this.listen(details, 'focusout', (event) => {
          if (!details.contains(event.relatedTarget)) details.open = false;
        });
      });
      this.listen(window, 'resize', () => this.updateMenuSpace(), { passive: true });
      this.listen(this.desktop, 'change', () => {
        this.closeDropdowns();
        if (this.desktop.matches && this.menuDialog.open) this.menuDialog.close();
      });
      this.resizeObserver = new ResizeObserver(() => {
        const height = this.host.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
        document.documentElement.style.setProperty('--chastar-header-height', `${height}px`);
      });
      this.resizeObserver.observe(this.host);
      this.updateMenuSpace();
      if (this.dataset.sticky === 'on-scroll-up') {
        this.listen(window, 'scroll', () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            const current = Math.max(0, window.scrollY);
            if (Math.abs(current - this.previousScroll) < 8) return;
            const panelOpen = this.searchDialog.open || this.menuDialog.open || !!this.querySelector('.chh-dropdown[open]');
            if (panelOpen) this.updateMenuSpace();
            const focused = this.contains(document.activeElement);
            this.host.classList.toggle('chh-host--hidden', !panelOpen && !focused && current > this.host.offsetHeight && current > this.previousScroll);
            this.previousScroll = current;
          });
        }, { passive: true });
      }
      this.listen(this, 'focusin', () => this.host.classList.remove('chh-host--hidden'));
    }

    disconnectedCallback() {
      this.searchDialog?.close();
      this.menuDialog?.close();
      if (this.savedOverflow !== undefined) document.documentElement.style.overflow = this.savedOverflow;
      this.listeners?.abort();
      this.listeners = null;
      this.resizeObserver?.disconnect();
      this.request?.abort();
      clearTimeout(this.debounce);
      this.dropdownTimers?.forEach((timer) => clearTimeout(timer));
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    listen(target, type, callback, options = {}) {
      target.addEventListener(type, callback, { ...options, signal: this.listeners.signal });
    }

    handleClick(event) {
      const opener = event.target.closest('[data-chh-open]');
      if (opener && this.contains(opener)) {
        const dialog = opener.dataset.chhOpen === 'search' ? this.searchDialog : this.menuDialog;
        if (typeof dialog.showModal !== 'function') return;
        event.preventDefault();
        this.openDialog(dialog, opener);
        return;
      }
      const closer = event.target.closest('[data-chh-close]');
      if (closer) closer.closest('dialog').close();
      if (event.target.closest('[data-chh-reset]')) {
        this.input.value = '';
        this.handleInput();
        this.input.focus();
      }
    }

    openDialog(dialog, opener) {
      if (dialog.open) return;
      this.closeDropdowns();
      this.host.classList.remove('chh-host--hidden');
      this.returnFocus = opener;
      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.querySelectorAll('[data-chh-open]').forEach((node) => node.setAttribute('aria-expanded', String(node.getAttribute('aria-controls') === dialog.id)));
      dialog.showModal();
      if (dialog === this.searchDialog) {
        this.input.focus();
        if (this.input.value.trim()) this.handleInput();
      }
    }

    handleClose(dialog) {
      this.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach((node) => node.setAttribute('aria-expanded', 'false'));
      if (this.savedOverflow !== undefined) {
        document.documentElement.style.overflow = this.savedOverflow;
        this.savedOverflow = undefined;
      }
      if (dialog === this.searchDialog) {
        this.request?.abort();
        clearTimeout(this.debounce);
        this.sequence += 1;
        this.results.removeAttribute('aria-busy');
        this.status.textContent = '';
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
      }
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
    }

    updateMenuSpace() {
      const navigation = this.querySelector('.chh-navigation');
      if (!navigation) return;
      const available = Math.max(120, window.innerHeight - navigation.getBoundingClientRect().bottom - 16);
      this.style.setProperty('--chh-menu-space', `${available}px`);
    }

    cancelDropdownClose(details) {
      clearTimeout(this.dropdownTimers?.get(details));
      this.dropdownTimers?.delete(details);
    }

    closeDropdowns(except) {
      this.querySelectorAll('.chh-dropdown').forEach((details) => {
        if (details === except) return;
        this.cancelDropdownClose(details);
        details.open = false;
      });
    }

    handleDropdown(event) {
      const details = event.target;
      if (!details.matches?.('.chh-dropdown')) return;
      details.querySelector('summary').setAttribute('aria-expanded', String(details.open));
      if (details.open && this.desktop.matches) {
        this.closeDropdowns(details);
        this.updateMenuSpace();
      }
    }

    handleDocumentKeys(event) {
      if (event.key === 'Escape') {
        const dialog = [this.searchDialog, this.menuDialog].find((panel) => panel.open);
        if (dialog) {
          event.preventDefault();
          dialog.close();
          return;
        }
      }
      if (event.key === 'Escape' && !this.searchDialog.open && !this.menuDialog.open) {
        const details = this.querySelector('.chh-dropdown[open]');
        if (details) {
          this.closeDropdowns();
          details.querySelector('summary').focus();
        }
      }
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
      if (event.target.closest?.('input, textarea, select, [contenteditable], [role="textbox"], cart-drawer.active')) return;
      if (this.searchDialog.open || this.menuDialog.open || document.querySelector('dialog[open]')) return;
      const opener = [...this.querySelectorAll('[data-chh-open="search"]')].find((node) => node.getClientRects().length);
      if (opener && typeof this.searchDialog.showModal === 'function') {
        event.preventDefault();
        this.openDialog(this.searchDialog, opener);
      }
    }

    handleInput() {
      clearTimeout(this.debounce);
      this.request?.abort();
      const sequence = ++this.sequence;
      const query = this.input.value.trim();
      this.selectedIndex = -1;
      this.reset.hidden = query.length === 0;
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.results.removeAttribute('aria-busy');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.intro.hidden = query.length > 0;
      this.status.textContent = '';
      if (query.length < 2 || this.dataset.predictiveEnabled !== 'true') return;
      this.debounce = setTimeout(() => this.loadSuggestions(query, sequence), 180);
    }

    async loadSuggestions(query, sequence) {
      if (sequence !== this.sequence || !this.searchDialog.open) return;
      if (this.cache.has(query)) {
        this.renderSuggestions(this.cache.get(query));
        return;
      }
      this.request = new AbortController();
      this.status.textContent = this.dataset.loadingLabel;
      this.results.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(this.dataset.predictiveUrl, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,page');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('section_id', 'chastar-header-search');
        const response = await fetch(url, { signal: this.request.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        if (sequence !== this.sequence || !this.searchDialog.open) return;
        const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-chh-result-content]');
        if (!fragment) throw new Error('Search response unavailable');
        const result = { html: fragment.innerHTML, count: Number(fragment.dataset.count) || 0 };
        this.cache.set(query, result);
        if (this.cache.size > 10) this.cache.delete(this.cache.keys().next().value);
        this.renderSuggestions(result);
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        this.status.textContent = this.dataset.errorLabel;
      } finally {
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }

    renderSuggestions(result) {
      this.results.innerHTML = result.html;
      this.results.hidden = result.count === 0;
      this.input.setAttribute('aria-expanded', String(result.count > 0));
      this.status.textContent = result.count === 0 ? this.dataset.emptyLabel : '';
      this.results.querySelectorAll('[role="option"]').forEach((option, index) => { option.id = `${this.id}-option-${index}`; });
    }

    handleSearchKeys(event) {
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (this.results.hidden || options.length === 0) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.selectedIndex = this.selectedIndex < 0 ? (direction > 0 ? 0 : options.length - 1) : (this.selectedIndex + direction + options.length) % options.length;
        options.forEach((option, index) => {
          const selected = index === this.selectedIndex;
          option.classList.toggle('is-active', selected);
          option.setAttribute('aria-selected', String(selected));
        });
        const option = options[this.selectedIndex];
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.selectedIndex >= 0) {
        event.preventDefault();
        options[this.selectedIndex].click();
      }
    }
  });
}
